import { FileTextOutlined } from '@ant-design/icons'
import { Form, Grid, Input, Modal, message } from 'antd'
import { useEffect, useState } from 'react'
import { apiCreateTestReport } from '../../../redux/testRun/testRun.service'
import { getCurrentProjectId } from '../../../utils/helpers'
import styles from './CreateTestReportModal.module.css'

const { TextArea } = Input

const CreateTestReportModal = ({
    isOpen = false,
    setOpen = () => {},
    projectId: projectIdProp,
    preselectedTestCaseIds = [],
    filterContext = {},
    onSuccess = () => {},
}) => {
    const [form] = Form.useForm()
    const screens = Grid.useBreakpoint()
    const modalWidth = screens.lg ? 560 : screens.md ? 520 : '94%'

    const [submitting, setSubmitting] = useState(false)
    const projectId = projectIdProp || getCurrentProjectId()
    const selectedCount = preselectedTestCaseIds.length
    const canSubmit = selectedCount > 0 && !submitting

    useEffect(() => {
        if (!isOpen) return
        form.resetFields()
    }, [isOpen, form])

    const handleClose = () => {
        if (submitting) return
        form.resetFields()
        setOpen(false)
    }

    const handleSubmit = async (values) => {
        if (!selectedCount) {
            message.warning('Select test cases from the list first')
            return
        }

        setSubmitting(true)
        try {
            const sprintId =
                filterContext?.sprint?.value ||
                filterContext?.sprint ||
                ''
            const tagIds = Array.isArray(filterContext?.tags)
                ? filterContext.tags
                : filterContext?.tags
                      ?.map((t) => t?.value || t)
                      .filter(Boolean) || []

            const payload = {
                name: values.name?.trim(),
                filter: {
                    project: projectId,
                    sprint: sprintId || null,
                    epic: [],
                    tags: tagIds,
                },
                search: {},
                testcaseIds: preselectedTestCaseIds,
            }

            const res = await apiCreateTestReport(payload)
            if (res?.data?.success) {
                message.success('Test report created successfully')
                handleClose()
                onSuccess(res?.data?.data)
            } else {
                message.error(
                    res?.data?.message || 'Failed to create test report'
                )
            }
        } catch {
            message.error('Failed to create test report')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Modal
            className={styles.modal}
            open={isOpen}
            onCancel={handleClose}
            destroyOnClose
            width={modalWidth}
            centered
            maskClosable={!submitting}
            closable={!submitting}
            title={
                <div className={styles.header}>
                    <div className={styles.headerIcon}>
                        <FileTextOutlined />
                    </div>
                    <div className={styles.headerText}>
                        <h2 className={styles.modalTitle}>
                            Create Test Report
                        </h2>
                        <p className={styles.subtitle}>
                            Name your report for the {selectedCount} selected
                            test case{selectedCount !== 1 ? 's' : ''}.
                        </p>
                    </div>
                </div>
            }
            footer={
                <div className={styles.footerBar}>
                    <span className={styles.footerHint}>
                        {selectedCount > 0
                            ? `${selectedCount} test case${selectedCount > 1 ? 's' : ''} will be included`
                            : 'Go back and select test cases first'}
                    </span>
                    <div className={styles.footerActions}>
                        <button
                            type="button"
                            className={styles.cancelButton}
                            onClick={handleClose}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={!canSubmit}
                            onClick={() => form.submit()}
                            className={`${styles.submitButton} ${
                                !canSubmit ? styles.submitButtonDisabled : ''
                            }`}
                        >
                            {submitting ? 'Creating...' : 'Create Report'}
                        </button>
                    </div>
                </div>
            }
        >
            <Form
                form={form}
                layout="vertical"
                className={styles.form}
                onFinish={handleSubmit}
                requiredMark={false}
            >
                <div className={styles.sections}>
                    <section className={styles.sectionCard}>
                        <div className={styles.sectionCardHeader}>
                            <div>
                                <p className={styles.sectionEyebrow}>
                                    Selected
                                </p>
                                <h3 className={styles.sectionTitle}>
                                    {selectedCount} test case
                                    {selectedCount !== 1 ? 's' : ''} chosen
                                </h3>
                            </div>
                            <span className={styles.selectionBadge}>
                                {selectedCount} included
                            </span>
                        </div>
                        <p className={styles.selectionSummary}>
                            These cases were selected from the test case list.
                            You can go back to change your selection before
                            creating the report.
                        </p>
                    </section>

                    <section className={styles.sectionCard}>
                        <div className={styles.sectionCardHeader}>
                            <div>
                                <p className={styles.sectionEyebrow}>
                                    Report details
                                </p>
                                <h3 className={styles.sectionTitle}>
                                    Name this run
                                </h3>
                            </div>
                        </div>
                        <div className={styles.formGrid}>
                            <Form.Item
                                label="Report name"
                                name="name"
                                className={styles.formGridFull}
                                rules={[
                                    {
                                        required: true,
                                        message: 'Report name is required',
                                    },
                                    {
                                        max: 120,
                                        message: 'Max 120 characters',
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="e.g. Sprint 12 regression run"
                                    allowClear
                                    autoFocus
                                />
                            </Form.Item>
                            <Form.Item
                                label="Note"
                                name="note"
                                className={styles.formGridFull}
                            >
                                <TextArea
                                    placeholder="Optional context for this report…"
                                    rows={2}
                                />
                            </Form.Item>
                        </div>
                    </section>
                </div>
            </Form>
        </Modal>
    )
}

export default CreateTestReportModal
